import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, useI18n } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Plus, LifeBuoy, Send, MessageCircle } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-amber-100 text-amber-700",
  in_progress: "bg-sky-100 text-sky-700",
  answered: "bg-emerald-100 text-emerald-700",
  closed: "bg-secondary text-muted-foreground",
};

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}

function NewTicketDialog({ t, lang, cats, onClose, onCreated }) {
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [saving, setSaving] = useState(false);

  const toggle = (k) => setSelected((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const submit = async () => {
    if (selected.length === 0) { toast.error(t("sup.err_cat")); return; }
    if (bodyText.trim().length < 3) { toast.error(t("sup.err_body")); return; }
    setSaving(true);
    try { await api.post("/coach/support/tickets", { categories: selected, subject: subject.trim() || null, body: bodyText.trim() }); toast.success(t("sup.created")); onCreated(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="sup-new-dialog">
        <DialogHeader>
          <DialogTitle>{t("sup.new")}</DialogTitle>
          <DialogDescription>{t("sup.new_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("sup.f_category")}</label>
            <div className="flex flex-wrap gap-1.5">
              {cats.map((c) => (
                <button key={c.key} onClick={() => toggle(c.key)} data-testid={`sup-cat-${c.key}`}
                  className={`rounded-full border px-3 py-1.5 text-xs ${selected.includes(c.key) ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                  {lang === "de" ? c.de : c.en}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("sup.f_subject")}</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("sup.f_subject_ph")} data-testid="sup-subject" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("sup.f_body")}</label>
            <Textarea className="min-h-[140px]" value={bodyText} onChange={(e) => setBodyText(e.target.value)} placeholder={t("sup.f_body_ph")} data-testid="sup-body" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="gap-1.5 rounded-full" onClick={submit} disabled={saving} data-testid="sup-submit"><Send className="h-4 w-4" /> {saving ? t("common.saving") : t("sup.submit")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TicketThread({ t, ticket, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/coach/support/tickets/${ticket.id}`); setData(d); } catch { setData({ ticket, messages: [] }); }
  }, [ticket]);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (reply.trim().length < 1) return;
    setBusy(true);
    try { await api.post(`/coach/support/tickets/${ticket.id}/messages`, { body: reply.trim() }); setReply(""); await load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };
  const close = async () => {
    if (!window.confirm(t("sup.close_confirm"))) return;
    try { await api.post(`/coach/support/tickets/${ticket.id}/close`); toast.success(t("sup.closed")); await load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const tk = data?.ticket || ticket;
  const isClosed = tk.status === "closed";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="sup-thread">
        <DialogHeader>
          <DialogTitle className="pr-6">{tk.subject}</DialogTitle>
          <DialogDescription>
            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[tk.status] || ""}`}>{t(`sup.status_${tk.status}`)}</span>
          </DialogDescription>
        </DialogHeader>
        {data === null ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : (
          <div className="space-y-2">
            {(data.messages || []).map((m) => (
              <div key={m.id} className={`rounded-2xl p-3 ${m.sender_role === "coach" ? "ml-6 bg-primary/10" : "mr-6 bg-secondary"}`} data-testid={`sup-msg-${m.id}`}>
                <p className="text-[11px] font-semibold text-muted-foreground">{m.sender_name} · {fmt(m.created_at)}</p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{m.body}</p>
              </div>
            ))}
          </div>
        )}
        {isClosed ? (
          <p className="rounded-xl bg-secondary p-3 text-center text-sm text-muted-foreground">{t("sup.is_closed")}</p>
        ) : (
          <div className="space-y-2">
            <Textarea className="min-h-[70px]" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("sup.reply_ph")} data-testid="sup-reply" />
            <div className="flex justify-between">
              <Button variant="outline" size="sm" className="rounded-full text-rose-600" onClick={close} data-testid="sup-close">{t("sup.close")}</Button>
              <Button size="sm" className="gap-1.5 rounded-full" onClick={send} disabled={busy} data-testid="sup-send"><Send className="h-3.5 w-3.5" /> {t("sup.send")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Support() {
  const { user } = useAuth();
  const t = useT();
  const { lang } = useI18n();
  usePageMeta(t("sup.meta_title"));
  const [rows, setRows] = useState(null);
  const [cats, setCats] = useState([]);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/support/tickets"); setRows(data?.tickets || []); } catch { setRows([]); }
  }, []);
  useEffect(() => {
    if (user?.role !== "coach") return;
    load();
    api.get("/support/constants").then(({ data }) => setCats(data?.coach_categories || [])).catch(() => setCats([]));
  }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="coach-support-page">
      <Seo title={t("sup.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="sup-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("sup.eyebrow")} title={t("sup.title")} subtitle={t("sup.subtitle")}
        action={<Button className="gap-1.5 rounded-full" onClick={() => setCreating(true)} data-testid="sup-new-btn"><Plus className="h-4 w-4" /> {t("sup.new")}</Button>} />

      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="sup-empty">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><LifeBuoy className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("sup.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("sup.empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((tk) => (
            <button key={tk.id} onClick={() => setActive(tk)} data-testid={`sup-ticket-${tk.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left hover:bg-accent/40">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><MessageCircle className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">{tk.subject}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{fmt(tk.last_message_at)} · {t("sup.n_msg", { n: tk.message_count })}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {tk.unread_by_coach > 0 && <span className="h-2 w-2 rounded-full bg-rose-500" />}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[tk.status] || ""}`}>{t(`sup.status_${tk.status}`)}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {creating && <NewTicketDialog t={t} lang={lang} cats={cats} onClose={() => setCreating(false)} onCreated={async () => { setCreating(false); await load(); }} />}
      {active && <TicketThread t={t} ticket={active} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}
